import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilArrayChanged } from '@examdojo/core/rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { BehaviorSubject, Observable, distinctUntilChanged, filter, map, of, take, tap } from 'rxjs';
import { ENTITLEMENT_SOURCE, EntitlementSource } from './entitlement.injection-token';
import { DEFAULT_ENTITLEMENT_MODE, Entitlement, EntitlementMode } from './entitlement.model';

@Injectable({ providedIn: 'root' })
export class EntitlementService {
  constructor(
    @Inject(ENTITLEMENT_SOURCE) entitlementSource: EntitlementSource,
    private readonly destroyRef: DestroyRef,
  ) {
    entitlementSource
      .pipe(
        tap((entitlements) => this.entitlements$$.next(entitlements)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private readonly entitlements$$ = new BehaviorSubject<Entitlement[] | null>(null);
  private readonly entitlements$ = this.entitlements$$.pipe(filter(isNotNullish), distinctUntilArrayChanged());

  readonly initialized$ = this.entitlements$$.pipe(filter(isNotNullish), map(Boolean), take(1));

  selectOne(key: Entitlement): Observable<boolean> {
    return this.entitlements$.pipe(
      map((current) => this.evaluate(current, [key], DEFAULT_ENTITLEMENT_MODE)),
      distinctUntilChanged(),
    );
  }

  selectMany(keys: readonly Entitlement[], mode: EntitlementMode = DEFAULT_ENTITLEMENT_MODE): Observable<boolean> {
    const normalizedKeys = [...keys];
    return this.entitlements$.pipe(
      map((current) => this.evaluate(current, normalizedKeys, mode)),
      distinctUntilChanged(),
    );
  }

  select(
    keys: Entitlement | Entitlement[] | null,
    mode: EntitlementMode = DEFAULT_ENTITLEMENT_MODE,
  ): Observable<boolean> {
    const normalizedKeys = this.normalizeKeys(keys);
    if (!normalizedKeys.length) {
      return of(false);
    }
    return this.selectMany(normalizedKeys, mode);
  }

  get(key: Entitlement): boolean {
    return this.entitlements$$.value?.includes(key) ?? false;
  }

  getAll(): Entitlement[] {
    return this.entitlements$$.value ?? [];
  }

  has(keys: Entitlement | readonly Entitlement[] | null, mode: EntitlementMode = DEFAULT_ENTITLEMENT_MODE): boolean {
    const normalizedKeys = this.normalizeKeys(keys);
    return this.evaluate(this.entitlements$$.value ?? [], normalizedKeys, mode);
  }

  private normalizeKeys(value: Entitlement | readonly Entitlement[] | null): Entitlement[] {
    if (value === null || value === undefined) {
      return [];
    }
    if (Array.isArray(value)) {
      return [...value];
    }
    return [value as Entitlement];
  }

  private evaluate(current: readonly Entitlement[], keys: readonly Entitlement[], mode: EntitlementMode) {
    if (!keys.length) {
      return false;
    }

    if (mode === 'any') {
      return keys.some((key) => current.includes(key));
    }

    return keys.every((key) => current.includes(key));
  }
}
